import React from 'react';
import { 
  ShoppingBag, Star, ShieldCheck, MapPin, 
  MessageCircle, Heart, Share2, Filter, 
  Search, Plus, Zap, AlertCircle, Coins, 
  ArrowRight, CheckCircle2, History, TrendingUp, Handshake, Info,
  Fingerprint, Upload, X, ShieldAlert, Lock, FileText, Camera
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { toast } from 'sonner';
import { SafePayEscrow } from './SafePayEscrow';
import { copyToClipboard } from '../utils/clipboard';

interface MarketplaceItem {
  id: string;
  title: string;
  price: number;
  currency: '₦' | 'Coins' | '$' | '£' | 'GH₵' | '€';
  category: 'Electronics' | 'Home' | 'Fashion' | 'Food' | 'Services' | 'Swap';
  condition: 'New' | 'Fairly Used' | 'Swap Only';
  location: string;
  seller: string;
  sellerScore: number;
  sellerBadge: string;
  image: string;
  postedAt: string;
  verified: boolean;
}

const ITEMS: MarketplaceItem[] = [
  {
    id: 'm1',
    title: 'iPhone 13 Pro Max (256GB)',
    price: 850000,
    currency: '₦',
    category: 'Electronics',
    condition: 'Fairly Used',
    location: 'Lekki Phase 1',
    seller: 'Chidi O.',
    sellerScore: 1850,
    sellerBadge: 'Elder',
    image: 'https://images.unsplash.com/photo-1632661674596-df8be070a5c5?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxpUGhvbmUlMjAxMyUyMHBybyUyMG1heHxlbnwxfHx8fDE3NzEyMzEyMTN8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
    postedAt: '2h ago',
    verified: true
  },
  {
    id: 'm2',
    title: 'Bag of Rice (50kg) - Foreign',
    price: 72000,
    currency: '₦',
    category: 'Food',
    condition: 'New',
    location: 'Ikeja Inside',
    seller: 'Mama Bisi',
    sellerScore: 2450,
    sellerBadge: 'Guardian',
    image: 'https://images.unsplash.com/photo-1586201375761-83865001e31c?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxyaWNlJTIwYmFnfGVufDF8fHx8MTc3MTIzMTIxNXww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
    postedAt: '5h ago',
    verified: true
  },
  {
    id: 'm3',
    title: 'PlayStation 4 Console',
    price: 350,
    currency: 'Coins',
    category: 'Swap',
    condition: 'Swap Only',
    location: 'Victoria Island',
    seller: 'Tunde W.',
    sellerScore: 2100,
    sellerBadge: 'Watchman',
    image: 'https://images.unsplash.com/photo-1486401899868-0e435ed85128?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwbGF5c3RhdGlvbiUyMDR8ZW58MXx8fHwxNzcxMjMxMjE4fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
    postedAt: 'Just now',
    verified: true
  },
  {
    id: 'm4',
    title: 'Samsung Galaxy Tab S9',
    price: 650,
    currency: '$',
    category: 'Electronics',
    condition: 'New',
    location: 'Houston, TX',
    seller: 'John D.',
    sellerScore: 1200,
    sellerBadge: 'Neighbor',
    image: 'https://images.unsplash.com/photo-1611078489935-0cb964de46d6?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxzYW1zdW5nJTIwdGFiJTIwczl8ZW58MXx8fHwxNzcxMjMxMjE4fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
    postedAt: '1h ago',
    verified: true
  },
  {
    id: 'm5',
    title: 'Dyson Airwrap Multistyler',
    price: 450,
    currency: '£',
    category: 'Home',
    condition: 'New',
    location: 'London, UK',
    seller: 'Emma W.',
    sellerScore: 3200,
    sellerBadge: 'Guardian',
    image: 'https://images.unsplash.com/photo-1620331713508-316270415d48?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxkZXlzb24lMjBhaXJ3cmFwfGVufDF8fHx8MTc3MTIzMTIxOHww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
    postedAt: '3h ago',
    verified: true
  }
];

interface MarketplaceProps {
  isSellingEnabled?: boolean;
  isSafePayEnabled?: boolean; // NEW: Control SafePay feature
  isOpen?: boolean;
  onClose?: () => void;
  user?: any;
}

export function NeighborhoodMarketplace({ isSellingEnabled = true, isSafePayEnabled = false, isOpen, onClose, user }: MarketplaceProps) {
  const [activeCategory, setActiveCategory] = React.useState<string>('All');
  const [search, setSearch] = React.useState('');
  const [selectedItem, setSelectedItem] = React.useState<MarketplaceItem | null>(null);
  const [isSafePayOpen, setIsSafePayOpen] = React.useState(false);
  const [isMarketVerified, setIsMarketVerified] = React.useState(false);
  const [showVerifyModal, setShowVerifyModal] = React.useState(false);
  const [showListModal, setShowListModal] = React.useState(false);
  const [verifying, setVerifying] = React.useState(false);
  const [nin, setNin] = React.useState('');
  const [docType, setDocType] = React.useState('NIN Slip');

  // Props isOpen, onClose, and user are available but not required for standalone usage
  // The component works both as a standalone widget and as a modal
  
  const categories = ['All', 'Electronics', 'Food', 'Swap', 'Services', 'Home'];

  const handleStartListing = () => {
    if (!isSellingEnabled) {
      toast.warning('Selling Suspended', { 
        description: 'Admin has temporarily suspended listing of new items. Please check back later.',
        icon: <ShieldAlert className="w-4 h-4 text-amber-500" />
      });
      return;
    }
    if (!isMarketVerified) {
      setShowVerifyModal(true);
    } else {
      setShowListModal(true);
    }
  };

  const handleVerify = (e: React.FormEvent) => {
    e.preventDefault();
    if (nin.length !== 11) {
      toast.error('Invalid NIN', { description: 'Please enter a valid 11-digit National Identification Number.' });
      return;
    }
    setVerifying(true);
    // Simulate NIN verification with NIMC servers
    setTimeout(() => {
      setVerifying(false);
      setIsMarketVerified(true);
      setShowVerifyModal(false);
      setShowListModal(true);
      toast.success('Identity Verified!', {
        description: 'Your NIN and ID document have been confirmed. You can now list items.',
        icon: <ShieldCheck className="w-4 h-4 text-green-500" />
      });
    }, 2500);
  };

  const filteredItems = ITEMS.filter(item => {
    const matchesSearch = item.title.toLowerCase().includes(search.toLowerCase()) || 
                          item.location.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = activeCategory === 'All' || item.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const handleBuy = (item: MarketplaceItem) => {
    if (['₦', '$', '£', 'GH₵', '€'].includes(item.currency)) {
      // Check if SafePay is enabled by admin
      if (!isSafePayEnabled) {
        toast.error('SafePay Unavailable', {
          description: 'SafePay Escrow has been temporarily disabled by admin. Please contact the seller directly via chat.',
          icon: <Lock className="w-4 h-4 text-red-500" />
        });
        return;
      }
      setSelectedItem(item);
      setIsSafePayOpen(true);
    } else {
      toast.success(`Swap interest sent for ${item.title}!`, {
        description: `Negotiating with ${item.seller} for a Sentlo Coin swap.`
      });
    }
  };

  return (
    <div className="relative flex flex-col gap-6 p-4 md:p-6 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <AnimatePresence>
        {isSafePayOpen && selectedItem && (
          <SafePayEscrow 
            isOpen={isSafePayOpen} 
            onClose={() => setIsSafePayOpen(false)}
            item={{
              title: selectedItem.title,
              price: selectedItem.price,
              seller: selectedItem.seller,
              currency: selectedItem.currency,
              country: selectedItem.location.includes('London') ? 'UK' : selectedItem.location.includes('New York') ? 'USA' : 'Nigeria'
            }}
            userBalance={500000} // Mock balance in NGN
            userCurrency="NGN" // User's base currency
            onConfirm={(amount) => {
              toast.success(`Transaction of ${amount.toLocaleString()} authorized!`);
            }}
          />
        )}

        {showVerifyModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6"
            onClick={() => setShowVerifyModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white w-full max-w-md rounded-[32px] overflow-hidden shadow-2xl relative"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 bg-amber-600 text-white flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-xl">
                    <ShieldAlert className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-black uppercase tracking-widest italic">Identity Verification</h3>
                    <p className="text-[10px] text-white/70 font-bold uppercase tracking-tighter italic">NIMC Compliance Required</p>
                  </div>
                </div>
                <button onClick={() => setShowVerifyModal(false)} className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/50 hover:text-white"><X className="w-4 h-4" /></button>
              </div>

              <div className="p-6 space-y-6">
                <div className="bg-amber-50 p-4 rounded-2xl border border-amber-100 flex gap-3">
                  <Info className="w-5 h-5 text-amber-600 shrink-0" />
                  <div className="space-y-1">
                    <p className="text-xs text-amber-800 leading-relaxed font-bold">
                      NIN is only required for P2P Sales & Swaps.
                    </p>
                    <p className="text-[10px] text-amber-700 leading-relaxed font-medium">
                      You can still update community prices, report traffic, and share light updates for <strong>FREE</strong> without any ID verification.
                    </p>
                  </div>
                </div>

                <form onSubmit={handleVerify} className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1 flex items-center gap-2">
                      <Fingerprint className="w-3 h-3" />
                      National Identification Number (NIN)
                    </label>
                    <input
                      required
                      type="text"
                      maxLength={11}
                      placeholder="Enter 11-digit NIN"
                      value={nin}
                      onChange={(e) => setNin(e.target.value.replace(/\D/g, ''))}
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-500/10 focus:border-amber-500 transition-all"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1 flex items-center gap-2">
                      <FileText className="w-3 h-3" />
                      ID Document Type
                    </label>
                    <select
                      value={docType}
                      onChange={(e) => setDocType(e.target.value)}
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-500/10 focus:border-amber-500 appearance-none"
                    >
                      <option value="NIN Slip">NIN Slip</option>
                      <option value="PVC">PVC (Voters Card)</option>
                      <option value="Driver's License">Driver's License</option>
                      <option value="Passport">International Passport</option>
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1 flex items-center gap-2">
                      <Camera className="w-3 h-3" />
                      Upload Document Photo
                    </label>
                    <div className="border-2 border-dashed border-gray-200 rounded-2xl p-6 flex flex-col items-center justify-center gap-2 hover:border-amber-500 hover:bg-amber-50/30 transition-all cursor-pointer group">
                      <div className="p-3 bg-gray-50 rounded-full group-hover:bg-amber-100 transition-colors">
                        <Upload className="w-5 h-5 text-gray-400 group-hover:text-amber-600" />
                      </div>
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Tap to upload / take photo</p>
                      <p className="text-[8px] text-gray-300 font-bold uppercase tracking-tight">JPG, PNG up to 5MB</p>
                    </div>
                  </div>

                  <button
                    disabled={verifying || nin.length !== 11}
                    className="w-full py-4 bg-amber-600 text-white rounded-2xl text-[11px] font-black uppercase tracking-[0.2em] shadow-lg active:scale-[0.98] transition-all disabled:opacity-50 disabled:grayscale flex items-center justify-center gap-2 mt-4"
                  >
                    {verifying ? (
                      <>
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                          className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full"
                        />
                        Verifying with NIMC...
                      </>
                    ) : (
                      <>
                        <ShieldCheck className="w-4 h-4" />
                        Verify & Continue
                      </>
                    )}
                  </button>
                </form>
              </div>

              <div className="p-4 bg-gray-50 border-t border-gray-100 flex items-center justify-center gap-4">
                <div className="flex items-center gap-1.5">
                  <Lock className="w-3 h-3 text-green-600" />
                  <span className="text-[8px] font-black text-gray-400 uppercase tracking-widest">End-to-end Encrypted</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <ShieldCheck className="w-3 h-3 text-green-600" />
                  <span className="text-[8px] font-black text-gray-400 uppercase tracking-widest">NIMC Verified Data</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}

        {showListModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6"
            onClick={() => setShowListModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white w-full max-w-lg rounded-[32px] overflow-hidden shadow-2xl relative"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center text-amber-600 shadow-sm">
                    <Plus className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-black uppercase tracking-widest italic text-gray-900">List New Item</h3>
                    <p className="text-[10px] text-green-600 font-bold uppercase tracking-tighter flex items-center gap-1">
                      <ShieldCheck className="w-3 h-3" />
                      Verified Seller Access
                    </p>
                  </div>
                </div>
                <button onClick={() => setShowListModal(false)} className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 hover:text-gray-900 transition-colors"><X className="w-4 h-4" /></button>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); setShowListModal(false); toast.success('Item Listed!', { description: 'Your item is now visible to verified neighbors.' }); }} className="p-6 space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-1.5">
                    <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">Title</label>
                    <input required type="text" placeholder="e.g. Clean PS4 Console" className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-500/10 focus:border-amber-500 transition-all" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">Price</label>
                    <input required type="number" placeholder="50,000" className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-500/10 focus:border-amber-500 transition-all" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">Currency</label>
                    <select className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-500/10 focus:border-amber-500 transition-all appearance-none">
                      <option value="₦">Naira (₦)</option>
                      <option value="Coins">Sentlo Coins</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">Description</label>
                  <textarea rows={3} placeholder="Tell neighbors about the item condition, why you're selling, etc." className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-500/10 focus:border-amber-500 transition-all resize-none" />
                </div>

                <div className="border-2 border-dashed border-gray-200 rounded-2xl p-8 flex flex-col items-center justify-center gap-2 hover:border-amber-500 hover:bg-amber-50/30 transition-all cursor-pointer">
                  <Upload className="w-6 h-6 text-gray-400" />
                  <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest">Upload Item Photos</p>
                </div>

                <button type="submit" className="w-full py-4 bg-amber-600 text-white rounded-2xl text-[11px] font-black uppercase tracking-[0.2em] shadow-lg active:scale-[0.98] transition-all">
                  Post Neighborhood Ad
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold flex items-center gap-2 text-gray-900">
            <ShoppingBag className="w-5 h-5 text-amber-600" />
            Neighborhood Swap & Shop
          </h2>
          {!isSellingEnabled ? (
            <span className="text-[10px] font-black text-red-600 bg-red-50 px-2.5 py-1 rounded-full flex items-center gap-1.5 border border-red-100">
              <Lock className="w-3 h-3" />
              MARKET SUSPENDED
            </span>
          ) : (
            <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-2 py-1 rounded-full flex items-center gap-1">
              <ShieldCheck className="w-3 h-3" />
              TRUST-VERIFIED
            </span>
          )}
        </div>
        <p className="text-sm text-gray-500">Buy, sell, and swap with neighbors you can trust.</p>
      </div>

      {/* SafePay Help Banner */}
      {isSafePayEnabled ? (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-start gap-3 bg-emerald-50 p-4 rounded-xl border border-emerald-200"
        >
          <div className="p-2 bg-emerald-100 rounded-lg shrink-0">
            <ShieldCheck className="w-5 h-5 text-emerald-700" />
          </div>
          <div className="flex-1">
            <h4 className="font-bold text-emerald-900 text-sm mb-1">How to Use SafePay Escrow</h4>
            <p className="text-xs text-emerald-700 leading-relaxed">
              <strong>Step 1:</strong> Browse items below and click "Buy" on any item with a currency symbol (₦, $, £).
              <br />
              <strong>Step 2:</strong> SafePay will open automatically to secure your payment in escrow.
              <br />
              <strong>Step 3:</strong> Funds are released to seller only after you confirm item delivery.
            </p>
          </div>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-start gap-3 bg-red-50 p-4 rounded-xl border border-red-200"
        >
          <div className="p-2 bg-red-100 rounded-lg shrink-0">
            <Lock className="w-5 h-5 text-red-700" />
          </div>
          <div className="flex-1">
            <h4 className="font-bold text-red-900 text-sm mb-1">SafePay Escrow Temporarily Disabled</h4>
            <p className="text-xs text-red-700 leading-relaxed">
              The admin has temporarily disabled SafePay Escrow. You can still browse items and contact sellers via chat. Coin swaps are still available!
            </p>
          </div>
        </motion.div>
      )}

      {/* Stats/Promo Bar */}
      <div className="flex items-center gap-4 bg-gray-50 p-3 rounded-xl border border-gray-100 overflow-x-auto no-scrollbar">
        <div className="flex items-center gap-2 shrink-0">
          <div className="p-2 bg-green-100 rounded-lg">
            <Handshake className="w-4 h-4 text-green-700" />
          </div>
          <div>
            <p className="text-[10px] font-bold text-gray-900 uppercase tracking-wider">Sentlo SafePay</p>
            <p className="text-[10px] text-gray-500">Escrow protected</p>
          </div>
        </div>
        <div className="w-px h-8 bg-gray-200 shrink-0" />
        <div className="flex items-center gap-2 shrink-0">
          <div className="p-2 bg-amber-100 rounded-lg">
            <Star className="w-4 h-4 text-amber-700" />
          </div>
          <div>
            <p className="text-[10px] font-bold text-gray-900 uppercase tracking-wider">Top Neighbor</p>
            <p className="text-[10px] text-gray-500">5+ Successful swaps</p>
          </div>
        </div>
        <div className="w-px h-8 bg-gray-200 shrink-0" />
        <div className="flex items-center gap-2 shrink-0">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Coins className="w-4 h-4 text-blue-700" />
          </div>
          <div>
            <p className="text-[10px] font-bold text-gray-900 uppercase tracking-wider">Coin Market</p>
            <p className="text-[10px] text-gray-500">Swap items for coins</p>
          </div>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col gap-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input 
            type="text"
            placeholder="Search items in your neighborhood..."
            className="w-full pl-10 pr-4 py-3 bg-gray-50 border-none rounded-xl text-sm focus:ring-2 focus:ring-amber-500 transition-all"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                activeCategory === cat 
                  ? 'bg-amber-100 text-amber-700' 
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Items Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <AnimatePresence mode="popLayout">
          {filteredItems.map((item) => (
            <motion.div 
              key={item.id}
              layout
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="flex flex-col rounded-xl border border-gray-100 overflow-hidden bg-white hover:border-amber-100 transition-colors group shadow-sm hover:shadow-md"
            >
              <div className="relative aspect-video overflow-hidden">
                <img 
                  src={item.image} 
                  alt={item.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-2 left-2 flex gap-2">
                  <span className="text-[10px] font-bold text-white bg-black/40 backdrop-blur-md px-2 py-1 rounded-md uppercase tracking-wider">
                    {item.condition}
                  </span>
                  {item.category === 'Swap' && (
                    <span className="text-[10px] font-bold text-white bg-blue-600 px-2 py-1 rounded-md uppercase tracking-wider flex items-center gap-1">
                      <Zap className="w-3 h-3" />
                      COIN SWAP
                    </span>
                  )}
                </div>
                <button className="absolute top-2 right-2 p-1.5 bg-white/80 backdrop-blur-md rounded-full text-gray-600 hover:text-red-500 transition-colors shadow-sm">
                  <Heart className="w-4 h-4" />
                </button>
              </div>

              <div className="p-4 flex flex-col gap-3">
                <div>
                  <div className="flex justify-between items-start mb-1">
                    <h3 className="font-semibold text-gray-900 group-hover:text-amber-700 transition-colors line-clamp-1">{item.title}</h3>
                    <p className="font-bold text-amber-600 shrink-0">
                      {item.currency}{item.price.toLocaleString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 text-[10px] text-gray-400">
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {item.location}
                    </span>
                    <span>•</span>
                    <span>{item.postedAt}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-xs font-bold text-amber-700 border-2 border-white shadow-sm shrink-0">
                    {item.seller[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1">
                      <p className="text-[10px] font-bold text-gray-900 truncate">{item.seller}</p>
                      {item.verified && <CheckCircle2 className="w-3 h-3 text-blue-500" />}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="flex items-center gap-0.5 text-[10px] font-bold text-green-600">
                        <TrendingUp className="w-3 h-3" />
                        {item.sellerScore}
                      </div>
                      <span className="text-[10px] text-gray-400 bg-gray-200 px-1.5 rounded uppercase tracking-tighter">
                        {item.sellerBadge}
                      </span>
                    </div>
                  </div>
                  <button className="p-1.5 hover:bg-amber-100 rounded-lg text-amber-600 transition-colors">
                    <MessageCircle className="w-4 h-4" />
                  </button>
                </div>

                <div className="flex gap-2">
                  <button 
                    onClick={() => handleBuy(item)}
                    className="flex-1 py-2 bg-amber-600 text-white text-xs font-bold rounded-lg hover:bg-amber-700 transition-colors flex items-center justify-center gap-2"
                  >
                    {item.category === 'Swap' ? 'Propose Swap' : 'I want this'}
                    <ArrowRight className="w-3 h-3" />
                  </button>
                  <button 
                    onClick={async () => {
                      const shareData = {
                        title: `Sentlo Marketplace: ${item.title}`,
                        text: `${item.title} for ${item.currency}${item.price.toLocaleString()} in ${item.location}`,
                        url: window.location.href,
                      };
                      if (navigator.share) {
                        navigator.share(shareData).catch(() => toast.info('Link copied to clipboard!'));
                      } else {
                        const success = await copyToClipboard(`${item.title} - ${item.currency}${item.price.toLocaleString()} in ${item.location}\n\nShared via Sentlo`);
                        if (success) toast.success('Link copied to clipboard!');
                      }
                    }}
                    className="p-2 border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors text-gray-500"
                  >
                    <Share2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Seller Promo */}
      <div className="bg-amber-50 p-4 rounded-xl border border-amber-100">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-600" />
            <h4 className="text-sm font-bold text-gray-900">Have something to sell or swap?</h4>
          </div>
          <span className="text-[10px] bg-amber-600 text-white px-2 py-0.5 rounded-full font-bold">+50 COINS</span>
        </div>
        <p className="text-xs text-gray-600 mb-4 leading-relaxed">
          Clean out your house and earn <strong>Sentlo Coins</strong>. Verified neighbors can list items for free and get prioritized in the local feed.
        </p>
        <button 
          onClick={handleStartListing}
          className="w-full py-3 bg-white border-2 border-amber-500 text-amber-700 text-sm font-bold rounded-xl hover:bg-amber-500 hover:text-white transition-all flex items-center justify-center gap-2 shadow-sm"
        >
          <Plus className="w-4 h-4" />
          List New Item
        </button>
      </div>

      <div className="flex items-center justify-center gap-4 text-[10px] text-gray-400">
        <div className="flex items-center gap-1">
          <Info className="w-3 h-3" />
          Escrow Active
        </div>
        <div className="flex items-center gap-1">
          <ShieldCheck className="w-3 h-3" />
          Zero Scam Policy
        </div>
        <div className="flex items-center gap-1">
          <MapPin className="w-3 h-3" />
          Local Area Only
        </div>
      </div>
    </div>
  );
}